import { Col, Flex, Row, Segmented, Table, Tag } from 'antd';
import { memo, useMemo, useState } from 'react';
import { CustomParagraph, CustomTitle } from '../../../../components';
import UATimesTable, { ServiceData } from '../../../../components/UATimesTable';
import type { ColumnsType } from 'antd/es/table';
import { isOpenNow } from './AirFranceTabs';
import { InfoItem } from './AirportSidebar';

interface FullDayNames {
  [key: string]: string;
}

const fullDayNames: FullDayNames = {
  'Mon.': 'Monday',
  'Tue.': 'Tuesday',
  'Wed.': 'Wednesday',
  'Thu.': 'Thursday',
  'Fri.': 'Friday',
  'Sat.': 'Saturday',
  'Sun.': 'Sunday',
};

interface ContactInfo {
  [key: string]: string | undefined;
  Email?: string;
  Sales_Email?: string;
  Sales_email?: string;
  Phone?: string;
  Phone_1?: string;
  Phone_2?: string;
  Firms_Code?: string;
}

interface OperatingHours {
  days?: string[];
  times?: string[];
}

interface UATabsContentData {
  name?: string;
  facilityAddress?: string;
  operatingHours?: OperatingHours;
  dropOffTime?: ServiceData[];
  pickupTime?: ServiceData[];
  contactInfo?: ContactInfo;
  exceptions?: string[];
}

interface UATabsContentProps {
  data?: UATabsContentData;
}

interface TableDataSource {
  key: string;
  day: string;
  hours: string;
}

type TabType = 'dropoff' | 'pickup';

/**
 * UATabsContent component displays airline facility information including
 * operating hours, contact info, and minimum times for dropoff/pickup
 */
const UATabsContent = memo<UATabsContentProps>(({ data }) => {
  const [selectedTab, setSelectedTab] = useState<TabType>('dropoff');

  const columns: ColumnsType<TableDataSource> = [
    {
      title: '',
      dataIndex: 'day',
      key: 'day',
      render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: '',
      dataIndex: 'hours',
      key: 'hours',
      align: 'right' as const,
    },
  ];

  const dataSource = useMemo<TableDataSource[]>(() => {
    const operatingHours = data?.operatingHours;
    if (!operatingHours || !operatingHours.days?.length) return [];

    return operatingHours.days.map((day, index) => ({
      key: `day-${index}`,
      day: fullDayNames[day] || day, // Map short name to full day name
      hours: operatingHours.times?.[index] || 'N/A',
    }));
  }, [data?.operatingHours]);

  const openNow = useMemo(() => {
    if (!dataSource.length) return false;

    const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

    const todayRow = dataSource.find((row) => row.day === today);
    if (!todayRow) return false;

    return isOpenNow(todayRow.day, todayRow.hours);
  }, [dataSource]);

  const contactFields = [
    { label: 'Email', key: 'Email' },
    { label: 'Sales email', key: 'Sales_email' },
    { label: 'Phone 1', key: 'Phone_1' },
    { label: 'Phone 2', key: 'Phone_2' },
    { label: 'Firms code', key: 'Firms_Code' },
  ];

  return (
    <div className='zl-tabs-content'>
      <Flex vertical gap={28}>
        <Flex vertical gap={4}>
          <CustomTitle
            className='zl-custom-title-sm'
            text={data?.name}
            fontSize='20px'
            fontWeight='700'
            lineHeight='24px'
            color='var(--text-primary)'
          />
          <div style={{ maxWidth: '180px' }}>
            <CustomTitle
              text={data?.facilityAddress}
              fontSize='12px'
              fontWeight='400'
              lineHeight='20px'
              color='var(--text-primary)'
              marginBottom='0'
              marginTop='0'
            />
          </div>
        </Flex>
        <Flex vertical gap={12}>
          <Flex gap={10} justify='space-between'>
            <CustomTitle
              text='Operating hours'
              fontSize='16px'
              fontWeight='600'
              lineHeight='20px'
              color='var(--text-primary)'
              marginBottom='0'
              marginTop='0'
            />
            {openNow && <Tag className='zl-custom-success-tag'>Open now</Tag>}
          </Flex>

          <Table
            className='zl-custom-times-table'
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            showHeader={false}
            rowHoverable={false}
            bordered
            size='middle'
            style={{ borderRadius: '8px' }}
          />
        </Flex>
        <Flex gap={12} vertical>
          <Flex gap={10} align='center' justify='space-between' wrap>
            <CustomTitle
              text='Minimum times'
              fontSize='16px'
              fontWeight='600'
              lineHeight='20px'
              color='var(--text-primary)'
              marginBottom='0'
            />
            <Segmented<TabType>
              className='zl-custom-segmented'
              value={selectedTab}
              onChange={setSelectedTab}
              options={[
                { label: 'Dropoff', value: 'dropoff' as const },
                { label: 'Pickup', value: 'pickup' as const },
              ]}
            />
          </Flex>
          <UATimesTable
            data={
              selectedTab === 'dropoff' ? data?.dropOffTime : data?.pickupTime
            }
            selectedTab={selectedTab}
          />
        </Flex>

        {/* contact info  */}

        <Flex gap={12} vertical>
          <CustomTitle
            text='Contact Info'
            fontSize='16px'
            fontWeight='600'
            lineHeight='20px'
            color='var(--text-primary)'
            marginBottom='0'
            marginTop='0'
          />

          <Flex
            vertical
            gap={12}
            className='w-full zl-company-info'
            style={{ padding: '16px' }}
          >
            <Row gutter={[24, 12]}>
              {contactFields.map(({ label, key }) => (
                <Col key={key} xs={24} sm={12} md={12} lg={6}>
                  <InfoItem
                    label={label}
                    value={data?.contactInfo?.[key] || '--'}
                  />
                </Col>
              ))}
            </Row>
          </Flex>
        </Flex>

        {/* exceptions */}
        {data?.exceptions?.length > 0 && (
          <div>
            <CustomTitle
              text='Exceptions'
              fontSize='16px'
              fontWeight='600'
              lineHeight='20px'
              color='var(--text-primary)'
              marginBottom='12px'
              marginTop='0'
            />
            <Row>
              <Col span={24}>
                <Flex vertical gap={16}>
                  <Flex
                    vertical
                    gap={12}
                    className='zl-company-info'
                    style={{ padding: '16px' }}
                  >
                    {data?.exceptions?.map((item, index) => (
                      <CustomParagraph
                        key={index}
                        text={item}
                        color='var(--text-primary)'
                        fontSize='12px'
                        fontWeight='400'
                        lineHeight='20px'
                        marginBottom={12}
                      />
                    ))}
                  </Flex>
                </Flex>
              </Col>
            </Row>
          </div>
        )}
      </Flex>
    </div>
  );
});

UATabsContent.displayName = 'UATabsContent';

export default UATabsContent;
